import * as THREE from 'three';

/**
 * Cielo procedural: hemisferio con gradiente vertical, disco solar, dos
 * capas de nubes instanciadas que se desplazan (cúmulos bajos y cirros
 * altos), campo de estrellas para noche, ciclo día-noche y matiz dorado
 * en la hora previa y posterior al orto/ocaso.
 *
 * Sin texturas externas: todo vertex/canvas. Cero dependencias ajenas a three.
 */

interface SkyPalette {
  zenithDay: number;
  horizonDay: number;
  zenithNight: number;
  horizonNight: number;
  sunDay: number;
  sunNight: number;
  sunGold: number;
  cloud: number;
  cloudShadow: number;
  cirrus: number;
  star: number;
}

const PALETTE: SkyPalette = {
  zenithDay: 0x3a86ff,
  horizonDay: 0xc8e8ff,
  zenithNight: 0x06122c,
  horizonNight: 0x1a2a4d,
  sunDay: 0xfff2cc,
  sunNight: 0xffd28a,
  sunGold: 0xffb46a,
  cloud: 0xffffff,
  cloudShadow: 0xb6c8d8,
  cirrus: 0xf2f7ff,
  star: 0xeaf1ff
};

/** Crea un skydoma con gradiente vertical e interpola día/noche según `t`. */
export class ProceduralSky {
  readonly group: THREE.Group;
  private readonly dome: THREE.Mesh;
  private readonly domeGeo: THREE.SphereGeometry;
  private readonly skyShader: THREE.ShaderMaterial;
  private readonly sun: THREE.Mesh;
  private readonly sunMat: THREE.MeshBasicMaterial;
  private readonly sunHalo: THREE.Sprite;
  private readonly sunLight: THREE.PointLight;
  private readonly clouds: THREE.Group;
  private readonly cirrus: THREE.Group;
  private readonly stars: THREE.Points;
  private readonly starsMat: THREE.PointsMaterial;
  /** Duración del ciclo completo (24 h de juego). Por defecto 4 min reales. */
  private cycleSeconds = 240;

  constructor(radius = 200) {
    this.group = new THREE.Group();
    this.domeGeo = new THREE.SphereGeometry(radius, 48, 32);
    this.skyShader = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        uZenithDay: { value: new THREE.Color(PALETTE.zenithDay) },
        uHorizonDay: { value: new THREE.Color(PALETTE.horizonDay) },
        uZenithNight: { value: new THREE.Color(PALETTE.zenithNight) },
        uHorizonNight: { value: new THREE.Color(PALETTE.horizonNight) },
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },
        uTransition: { value: 0 },
        uSunGlow: { value: new THREE.Color(PALETTE.sunDay) },
        uSunGold: { value: new THREE.Color(PALETTE.sunGold) },
        uGoldMix: { value: 0 }
      },
      vertexShader: `
        varying vec3 vWorldDir;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldDir = normalize(wp.xyz);
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: `
        uniform vec3 uZenithDay;
        uniform vec3 uHorizonDay;
        uniform vec3 uZenithNight;
        uniform vec3 uHorizonNight;
        uniform vec3 uSunDir;
        uniform float uTransition;
        uniform vec3 uSunGlow;
        uniform vec3 uSunGold;
        uniform float uGoldMix;
        varying vec3 vWorldDir;

        void main() {
          vec3 dir = normalize(vWorldDir);
          float h = clamp(dir.y, -0.05, 1.0);
          float upT = pow(max(h, 0.0), 0.65);
          vec3 dayMix = mix(uHorizonDay, uZenithDay, upT);
          vec3 nightMix = mix(uHorizonNight, uZenithNight, upT);
          float t = clamp(uTransition, 0.0, 1.0);
          vec3 sky = mix(nightMix, dayMix, t);

          // Banda dorada pegada al horizonte cuando el sol está bajo.
          float horizonBand = 1.0 - smoothstep(0.0, 0.35, max(h, 0.0));
          vec3 goldTint = mix(uSunGold, uHorizonDay, 0.4);
          sky = mix(sky, goldTint, horizonBand * uGoldMix);

          // Halo solar en dos pasadas: una amplia y suave + un núcleo duro.
          float sunDot = clamp(dot(dir, normalize(uSunDir)), 0.0, 1.0);
          float haloSoft = pow(sunDot, 8.0);
          float haloCore = pow(sunDot, 220.0);
          sky += uSunGlow * haloSoft * 0.5 * t;
          sky += uSunGlow * haloCore * 1.2 * t;
          sky += uSunGold * haloSoft * 0.6 * uGoldMix;

          gl_FragColor = vec4(sky, 1.0);
        }
      `
    });
    this.dome = new THREE.Mesh(this.domeGeo, this.skyShader);
    this.dome.renderOrder = -1;
    this.group.add(this.dome);

    // Disco solar: esfera más grande para que se lea como sol, no como punto.
    this.sunMat = new THREE.MeshBasicMaterial({ color: PALETTE.sunDay, transparent: true, opacity: 0.95, depthWrite: false });
    this.sun = new THREE.Mesh(new THREE.SphereGeometry(7.5, 24, 16), this.sunMat);
    this.sun.renderOrder = -1;
    this.sun.frustumCulled = false;
    this.group.add(this.sun);

    // Brillo del sol: sprite procedural billboard que se ve aunque la cámara
    // no apunte exactamente al disco.
    this.sunHalo = new THREE.Sprite(new THREE.SpriteMaterial({
      map: this.makeSunHaloTexture(),
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      opacity: 0.85
    }));
    this.sunHalo.scale.set(36, 36, 1);
    this.sunHalo.renderOrder = -1;
    this.group.add(this.sunHalo);

    // Luz puntual del sol (sin sombras para mantener el coste bajo).
    this.sunLight = new THREE.PointLight(PALETTE.sunDay, 0.8, 0, 1.2);
    this.sunLight.castShadow = false;
    this.group.add(this.sunLight);

    // Nubes: capa baja (cúmulos) y capa alta (cirros). Las dos orbitan a
    // velocidades distintas para que el cielo siempre tenga movimiento.
    this.clouds = this.buildClouds(34, 18, 40, 8, 14, 32, false);
    this.cirrus = this.buildClouds(14, 50, 110, 4, 20, 70, true);
    this.group.add(this.clouds, this.cirrus);

    // Estrellas: points distribuidos en el hemisferio, visibles solo de noche.
    this.stars = this.buildStars(540);
    this.starsMat = this.stars.material as THREE.PointsMaterial;
    this.starsMat.transparent = true;
    this.starsMat.opacity = 0;
    this.group.add(this.stars);
  }

  /** Controlador de ciclo. Avanza el tiempo y actualiza paletas. */
  update(elapsedSec: number): void {
    const phase = ((elapsedSec / this.cycleSeconds) % 1) * Math.PI * 2;
    const cy = Math.cos(phase);
    const sy = Math.sin(phase);
    const dir = new THREE.Vector3(cy, sy, 0.4 * Math.cos(phase * 0.5)).normalize();
    // Transición día(1) ↔ noche(0) suave cerca del horizonte.
    const transition = Math.max(0, Math.min(1, (sy + 0.18) / 0.9));
    const tEase = transition;
    // Hora dorada: el domo se tiñe cuando el sol está pegado al horizonte.
    const goldMix = Math.max(0, Math.min(1, 1.0 - Math.abs(sy) * 4.0)) * 0.55;
    this.skyShader.uniforms.uSunDir.value.copy(dir);
    this.skyShader.uniforms.uTransition.value = tEase;
    this.skyShader.uniforms.uSunGlow.value.set(PALETTE.sunNight).lerp(new THREE.Color(PALETTE.sunDay), tEase);
    this.skyShader.uniforms.uGoldMix.value = goldMix;
    const sunDist = 180;
    this.sun.position.copy(dir).multiplyScalar(sunDist);
    this.sunHalo.position.copy(dir).multiplyScalar(sunDist);
    this.sunHalo.material.color.copy(this.skyShader.uniforms.uSunGlow.value as THREE.Color);
    this.sunHalo.material.opacity = 0.25 + 0.65 * Math.max(goldMix * 1.4, tEase);
    this.sunLight.position.copy(dir).multiplyScalar(80);
    this.sunLight.color.set(PALETTE.sunNight).lerp(new THREE.Color(PALETTE.sunDay), tEase);
    this.sunLight.intensity = 0.2 + 0.9 * tEase;
    // El disco se desvanece bajo el horizonte para no dejar un ámbar
    // flotando de noche.
    const sunAbove = Math.max(0, sy * 1.4 + 0.05);
    this.sunMat.color.set(PALETTE.sunNight).lerp(new THREE.Color(PALETTE.sunDay), tEase);
    this.sunMat.opacity = Math.max(0, Math.min(1, sunAbove * 1.2));
    this.sun.visible = this.sunMat.opacity > 0.02;

    // Nubes: rotación lenta + billboard por capa.
    this.animateClouds(this.clouds, elapsedSec, 1.0);
    this.animateClouds(this.cirrus, elapsedSec, 0.35);
    // Estrellas: visibles solo de noche, con parpadeo suave.
    this.starsMat.opacity = Math.max(0, (1 - tEase) * (0.6 + 0.4 * Math.max(0, -sy * 2.2 + 0.2)));
    this.stars.children.forEach((p, i) => {
      const s = p as THREE.Points;
      const base = s.scale.x;
      s.scale.setScalar(base + Math.sin(elapsedSec * 1.5 + i) * 0.04);
    });
  }

  /** Anima las nubes de un grupo: orbitan en planos horizontales a
   *  velocidades distintas, y flotan ligeramente arriba/abajo. */
  private animateClouds(grp: THREE.Group, elapsedSec: number, speedScale: number): void {
    grp.children.forEach((c, i) => {
      const cloud = c as THREE.Mesh;
      const user = cloud.userData as { baseY: number; orbitR: number; speed: number; angle0: number };
      const phase2 = (elapsedSec * user.speed * speedScale + user.angle0);
      cloud.position.x = Math.cos(phase2) * user.orbitR;
      cloud.position.z = Math.sin(phase2) * user.orbitR;
      cloud.position.y = user.baseY + Math.sin(elapsedSec * 0.25 + i) * 0.6;
      // Billboard: rota el plano para que mire a la cámara todo el rato.
      const rot = elapsedSec * 0.04 + i;
      cloud.rotation.set(rot * 0.05, rot, 0);
    });
  }

  /** Nubes instanciadas con textura procedural. `wispy` true => cirros finos. */
  private buildClouds(
    count: number,
    sizeMin: number,
    sizeMax: number,
    wMin: number,
    wMax: number,
    alt: number,
    wispy: boolean
  ): THREE.Group {
    const grp = new THREE.Group();
    const tex = this.makeCloudTexture(wispy);
    for (let i = 0; i < count; i++) {
      const isShadow = !wispy && i % 5 === 0;
      const color = wispy
        ? PALETTE.cirrus
        : isShadow
          ? PALETTE.cloudShadow
          : PALETTE.cloud;
      const opacity = wispy ? 0.45 + Math.random() * 0.2 : 0.75 + Math.random() * 0.2;
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        opacity,
        color
      });
      const w = wMin + Math.random() * (wMax - wMin);
      const h = sizeMin + Math.random() * (sizeMax - sizeMin);
      const cloud = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
      // Posición inicial: orbitar en x-z. El `animateClouds` reescribe esto
      // cada frame, pero necesitamos `baseY/orbitR/...` para la animación.
      const orbitR = wispy ? 90 + Math.random() * 60 : 65 + Math.random() * 60;
      const baseY = alt + (wispy ? Math.random() * 12 : -4 + Math.random() * 8);
      const angle0 = Math.random() * Math.PI * 2;
      const speed = (wispy ? 0.05 : 0.02) + Math.random() * 0.05;
      cloud.userData = { baseY, orbitR, speed, angle0 };
      cloud.position.set(Math.cos(angle0) * orbitR, baseY, Math.sin(angle0) * orbitR);
      cloud.renderOrder = -1;
      grp.add(cloud);
    }
    grp.name = wispy ? 'cirrus' : 'clouds';
    return grp;
  }

  /** Genera una textura procedural tipo cúmulo mediante canvas 2D. */
  private makeCloudTexture(wispy: boolean): THREE.CanvasTexture {
    const c = document.createElement('canvas');
    c.width = 256; c.height = 128;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, c.width, c.height);
    if (wispy) {
      const streaks = 14;
      for (let i = 0; i < streaks; i++) {
        const cx = Math.random() * c.width;
        const cy = 30 + Math.random() * 70;
        const len = 60 + Math.random() * 180;
        const grad = ctx.createLinearGradient(cx - len / 2, cy, cx + len / 2, cy);
        grad.addColorStop(0, 'rgba(255,255,255,0)');
        grad.addColorStop(0.5, 'rgba(255,255,255,0.6)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(cx - len / 2, cy - 1, len, 2 + Math.random() * 3);
      }
    } else {
      const blobCount = 18;
      for (let i = 0; i < blobCount; i++) {
        const cx = Math.random() * c.width;
        const cy = 40 + Math.random() * 50;
        const r = 18 + Math.random() * 38;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, 'rgba(255,255,255,0.95)');
        grad.addColorStop(0.6, 'rgba(255,255,255,0.55)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      }
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.ClampToEdgeWrapping;
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  /** Campo de estrellas en el hemisferio superior con sprite procedural. */
  private buildStars(count: number): THREE.Points {
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      // Concentra más densidad cerca del cenit (hacia arriba).
      const phi = Math.acos(Math.random() * 0.85 + 0.05);
      const r = 165 + Math.random() * 8;
      positions[i * 3] = Math.sin(phi) * Math.cos(theta) * r;
      positions[i * 3 + 1] = Math.cos(phi) * r * 0.95;
      positions[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * r;
      sizes[i] = 0.5 + Math.random() * 1.8;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    const mat = new THREE.PointsMaterial({
      color: PALETTE.star,
      size: 1.1,
      sizeAttenuation: false,
      transparent: true,
      depthWrite: false,
      map: this.makeStarTexture()
    });
    return new THREE.Points(geo, mat);
  }

  /** Pequeño sprite radial blanco para los puntos-estrella. */
  private makeStarTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas');
    c.width = 32; c.height = 32;
    const ctx = c.getContext('2d')!;
    const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(220,235,255,0.6)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 32, 32);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  /** Resplandor amplio y suave que rodea al disco del sol. */
  private makeSunHaloTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas');
    c.width = 256; c.height = 256;
    const ctx = c.getContext('2d')!;
    const grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    grad.addColorStop(0.0, 'rgba(255,238,200,0.95)');
    grad.addColorStop(0.25, 'rgba(255,210,140,0.4)');
    grad.addColorStop(0.6, 'rgba(255,170,90,0.12)');
    grad.addColorStop(1.0, 'rgba(255,170,90,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 256, 256);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  dispose(): void {
    this.domeGeo.dispose();
    this.skyShader.dispose();
    this.sunMat.dispose();
    (this.sun.geometry as THREE.BufferGeometry).dispose();
    const haloMat = this.sunHalo.material as THREE.SpriteMaterial;
    if (haloMat.map) haloMat.map.dispose();
    haloMat.dispose();
    for (const grp of [this.clouds, this.cirrus]) {
      grp.children.forEach((c) => {
        const m = c as THREE.Mesh;
        (m.geometry as THREE.BufferGeometry).dispose();
        (m.material as THREE.Material).dispose();
      });
    }
    this.stars.geometry.dispose();
    (this.stars.material as THREE.Material).dispose();
  }
}
